import { useState, useMemo } from 'react';
import {
    FormControl,
    Stack,
    FormLabel,
    HStack,
    useDisclosure,
} from '@chakra-ui/react';
import { Button, Text } from 'components/v4';
import { Field } from 'formik';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import { useCompanyForm } from '../../hooks/useCompanyForm';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import { formLabelStyling } from 'components/forms/new/formStyles';
import { getBillingFieldPaths, getBillingSettings } from '../../constants';
import { stackSpacing, stackStyling } from 'components/forms/new/formStyles';
import ChangeBillingDay from '../../../edit/ChangeBillingDay';

const BillTo = ({ darkMode, errors, isCompany }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { formData, updateFormData, mode } = useCompanyForm();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const billingFieldPaths = getBillingFieldPaths(mode);
    const billingSettings = getBillingSettings(formData, mode);
    const billingEntityIDs = {
        companyID: mode === 'Company' ? formData?.id : undefined,
        partnerID: mode === 'Partner' ? formData?.id : undefined,
        wholesalerID: mode === 'Wholesaler' ? formData?.id : undefined,
    };

    const showBillTo = mode !== 'Wholesaler';
    const hasError = showBillTo && !!errors?.billTo;

    // Example billing days, replace with your actual source if needed
    const billingDayDropdown = useMemo(
        () =>
            Array.from({ length: 28 }, (_, i) => ({
                value: (i + 1).toString(),
                label: (i + 1).toString(),
                id: (i + 1).toString(),
            })),
        [],
    );

    return (
        <>
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={hasError}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)} mb={0}>
                        {showBillTo ? 'Bill To' : 'Billing Day'}
                    </FormLabel>
                    <HStack
                        flex={1}
                        maxWidth="400px"
                        display="flex"
                        alignItems="center">
                        <>
                            {showBillTo && (
                                <Field
                                    component={DropDown}
                                    id={billingFieldPaths.BILL_TO}
                                    name={billingFieldPaths.BILL_TO}
                                    noPadding
                                    data={(isCompany
                                        ? ['Company', 'Partner']
                                        : ['Partner', 'Wholesaler']
                                    ).map((val) => ({ id: val, name: val }))}
                                    currentId={billingSettings?.billTo}
                                    selectedId="id"
                                    shownValue="name"
                                    placeholder="Bill To..."
                                    handleSelectChange={(item) => {
                                        updateFormData(
                                            billingFieldPaths.BILL_TO,
                                            item?.id,
                                        );
                                    }}
                                    width="100%"
                                    isLoading={false}
                                    darkMode={darkMode}
                                />
                            )}
                            {showBillTo && (
                                <Text fontWeight={500}>{'Billing Day: '}</Text>
                            )}
                            {billingSettings?.billingDay || '0'}
                        </>
                        <Button
                            size="sm"
                            height="30px"
                            ml={showBillTo ? 0 : 4}
                            onClick={onOpen}>
                            Update
                        </Button>
                    </HStack>
                    {isHovered && (
                        <FieldToolTip
                            label="Opens up a modal to change the billing day for this entity"
                            ariaLabel="billto-tip"
                            left="25px"
                            top="25px"
                        />
                    )}
                </Stack>
            </FormControl>
            <ChangeBillingDay
                openModal={isOpen}
                handleCloseModal={onClose}
                mode={mode}
                billingDayDropdown={billingDayDropdown}
                currentBillingDay={billingSettings?.billingDay}
                companyID={billingEntityIDs.companyID}
                partnerID={billingEntityIDs.partnerID}
                wholesalerID={billingEntityIDs.wholesalerID}
                handleRefreshModal={(data) => {
                    const newBillingDay =
                        data[`${mode?.toLowerCase()}BillingSettings`]
                            .billingDay;
                    updateFormData(
                        billingFieldPaths.BILLING_DAY,
                        newBillingDay,
                    );
                }}
            />
        </>
    );
};
export default BillTo;
